import { CommonModule, KeyValuePipe, NgOptimizedImage } from '@angular/common';
import { Component, inject, input, InputSignal, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  BasketService,
  ColorService,
  ConfigService,
  GsDropdownMenuItem,
  GsResponsiveDirective,
  ImageService,
  InformationService,
  LanguageService,
  MyChainstoreService,
  StyleService,
  SystemService,
  WatchlistService
} from 'libs/shared/src';
import { GsIconComponent } from 'libs/shared/src/components/gs-icon/gs-icon.component';
import { OrderByPipe } from 'libs/shared/src/pipes/orderBy.pipe';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { MegaMenuComponent } from '../../megamenu/megamenu.component';
import { MyChainstoreComponent } from '../../my-chainstore/my-chainstore.component';
import { TypeaheadComponent } from '../../typeahead/typeahead.component';
import { HeaderSidebarComponent } from '../components/header-sidebar/header-sidebar.component';
import { TopbarComponent } from '../components/topbar/topbar.component';
import { TopbarHeightDirective } from '../directives/topbar-height.directive';
import { HeaderService } from '../services/header.service';

@Component({
  selector: 'header-basic',
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TypeaheadComponent,
    TranslatePipe,
    DropdownComponent,
    GsIconComponent,
    MegaMenuComponent,
    MyChainstoreComponent,
    HeaderSidebarComponent,
    OrderByPipe,
    TopbarComponent,
    TopbarHeightDirective,
    GsResponsiveDirective
  ],
  providers: [KeyValuePipe],
  templateUrl: './header-basic.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      display: block;
    }
  `
})
export class HeaderBasicComponent {
  protected readonly basket = inject(BasketService);
  protected readonly config = inject(ConfigService);
  protected readonly colorService = inject(ColorService);
  protected readonly imageService = inject(ImageService);
  protected readonly infoService = inject(InformationService);
  protected readonly languageService = inject(LanguageService);
  protected readonly styleService = inject(StyleService);
  protected readonly system = inject(SystemService);
  protected readonly watchlist = inject(WatchlistService);
  protected readonly headerService = inject(HeaderService);
  protected readonly myChainstoreService = inject(MyChainstoreService);

  // Inputs
  /** Scroll position on main page */
  scrollPosition: InputSignal<number> = input.required<number>();

  // Properties & Signals
  protected readonly topbarOpen = signal(true);

  // Methods
  toggleTopbar(state?: boolean): boolean {
    state ??= !this.topbarOpen();
    this.topbarOpen.set(state);
    return state;
  }

  /**
   * Handles the click event on a mainbar action.
   * @param action that was clicked
   * @returns true if the action was handled, false otherwise
   */
  handleActionClick(name: string, ev: Event): boolean {
    ev.preventDefault();
    return this.headerService.performAction(name);
  }

  /**
   * Handles the mouse enter event on a dropdown item.
   * @param dropdownComp The dropdown component
   * @param event The mouse event
   * @param menuItem The dropdown item being hovered.
   */
  toggleDropdownOf(dropdownComp: DropdownComponent | MegaMenuComponent, event: MouseEvent, menuItem: GsDropdownMenuItem) {
    if (menuItem.items.length === 0) {
      return;
    }

    const isMouseEnter = event.type === 'mouseenter';
    const isMouseLeave = event.type === 'mouseleave';
    if (isMouseEnter || isMouseLeave) {
      dropdownComp.toggleDropdown(isMouseEnter, event, menuItem, false);
      return;
    }

    dropdownComp.toggleDropdown(undefined, event, menuItem, false);
  }
}
